import React from 'react';
import type { ColumnHeaderOption } from './ColumnHeaderMenu';

/**
 * Client-side sort and value filters for a loaded table, driven by
 * `ColumnHeaderMenu` headers (see `ColumnQueryHeaders`). Use only when the
 * table already holds every row in scope; server-paged lists must filter in
 * their query instead.
 */
export type ColumnQueryColumn<Row> = {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  /** Omit to make the column sort-only. */
  filterValue?: (row: Row) => string;
  sortValue: (row: Row) => string | number;
};
export type ColumnQueryFilters = Partial<Record<string, string[]>>;
export type ColumnQuerySort = { key: string; direction: 'asc' | 'desc' } | null;

const compare = (left: string | number, right: string | number) =>
  typeof left === 'number' && typeof right === 'number'
    ? left - right
    : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });

/** Each distinct value once, in the column's natural (sort) order. */
export function columnQueryOptions<Row>(rows: readonly Row[], column: ColumnQueryColumn<Row>): ColumnHeaderOption[] {
  if (!column.filterValue) return [];
  const seen = new Map<string, { count: number; sort: string | number }>();
  for (const row of rows) {
    const value = column.filterValue(row);
    const current = seen.get(value);
    if (current) current.count += 1;
    else seen.set(value, { count: 1, sort: column.sortValue(row) });
  }
  return [...seen.entries()].sort((a, b) => compare(a[1].sort, b[1].sort))
    .map(([value, { count }]) => ({ value, label: value, count }));
}

export function applyColumnQuery<Row>(
  rows: readonly Row[], columns: ReadonlyArray<ColumnQueryColumn<Row>>, filters: ColumnQueryFilters, sort: ColumnQuerySort,
): Row[] {
  const active = columns.flatMap(column => {
    const values = filters[column.key];
    return column.filterValue && values?.length ? [{ read: column.filterValue, values: new Set(values) }] : [];
  });
  const filtered = active.length ? rows.filter(row => active.every(({ read, values }) => values.has(read(row)))) : [...rows];
  const sortColumn = sort ? columns.find(column => column.key === sort.key) : undefined;
  if (!sort || !sortColumn) return filtered;
  const sign = sort.direction === 'asc' ? 1 : -1;
  return filtered.sort((a, b) => sign * compare(sortColumn.sortValue(a), sortColumn.sortValue(b)));
}

export function useColumnQuery<Row>(rows: readonly Row[], columns: ReadonlyArray<ColumnQueryColumn<Row>>) {
  const [filters, setFilters] = React.useState<ColumnQueryFilters>({});
  const [sort, setSort] = React.useState<ColumnQuerySort>(null);
  const options = React.useMemo(
    () => new Map(columns.map(column => [column.key, columnQueryOptions(rows, column)])),
    [columns, rows],
  );
  const visibleRows = React.useMemo(() => applyColumnQuery(rows, columns, filters, sort), [columns, filters, rows, sort]);
  const querying = sort != null || Object.values(filters).some(values => Boolean(values?.length));
  const setFilter = React.useCallback((key: string, values: string[]) => setFilters(current => ({ ...current, [key]: values })), []);
  const clear = React.useCallback(() => { setFilters({}); setSort(null); }, []);
  return { filters, sort, setSort, setFilter, clear, options, rows: visibleRows, querying };
}

export type ColumnQueryState = ReturnType<typeof useColumnQuery>;
